import { OptionalArgTuple } from '@deltasierra/type-utilities';
import { IntegrationConnectionStatus } from '../status';

export interface IntegrationConnectionConfiguration<
    TypeName,
    ConfigurationTypeUnion extends {
        type: ConfigurationTypes;
    },
    ConfigurationTypes = ConfigurationTypeUnion['type'],
> {
    type: TypeName;

    configuration: ConfigurationTypeUnion;
    status: IntegrationConnectionStatus;
}

export interface IntegrationConnectionConfigurationConstructor<
    R,
    ConfigurationTypeUnion extends {
        type: ConfigurationTypes;
    },
    ConfigurationTypes = ConfigurationTypeUnion['type'],
> {
    new (configuration: ConfigurationTypeUnion, status: IntegrationConnectionStatus): R;

    of<
        T extends ConfigurationTypes,
        JointType = ConfigurationTypeUnion & {
            type: T;
        },
    >(
        type: T,
        status: IntegrationConnectionStatus,
        ...data: OptionalArgTuple<keyof Omit<JointType, 'type'> extends never ? undefined : Omit<JointType, 'type'>>
    ): R;
}

export function createIntegrationConnectionConfigurationClassMixin<
    ConfigurationTypeUnion extends {
        type: ConfigurationTypes;
    },
    TypeName,
    ConfigurationTypes = ConfigurationTypeUnion['type'],
>(
    typeName: TypeName,
): IntegrationConnectionConfigurationConstructor<
    IntegrationConnectionConfiguration<TypeName, ConfigurationTypeUnion, ConfigurationTypes>,
    ConfigurationTypeUnion,
    ConfigurationTypes
> {
    class IntegrationConnectionConfigurationMixin
        implements IntegrationConnectionConfiguration<TypeName, ConfigurationTypeUnion, ConfigurationTypes> {
        public readonly type: TypeName;

        public constructor(
            public readonly configuration: ConfigurationTypeUnion,
            public readonly status: IntegrationConnectionStatus,
        ) {
            this.type = typeName;
        }

        public static of<
            T extends ConfigurationTypes,
            JointType = ConfigurationTypeUnion & {
                type: T;
            },
        >(
            type: T,
            status: IntegrationConnectionStatus,
            ...data: OptionalArgTuple<keyof Omit<JointType, 'type'> extends never ? undefined : Omit<JointType, 'type'>>
        ): IntegrationConnectionConfigurationMixin {
            return new IntegrationConnectionConfigurationMixin(
                {

                    ...data?.[0] ?? {},
                    type,
                } as ConfigurationTypeUnion & {
                    type: T;
                },
                status,
            );
        }
    }

    return IntegrationConnectionConfigurationMixin;
}
